'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';
import MarketTable from '@/components/MarketTable';
import CoinLogo from '@/components/CoinLogo';
import EconomicCalendar from '@/components/EconomicCalendar';
import SectorsView from '@/components/SectorsView';
import LiqHeatmap from '@/components/LiqHeatmap';
import PullToRefresh from '@/components/PullToRefresh';
import { ListSkeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { useQueryClient } from '@tanstack/react-query';

// Protocol/symbol normalization map (same as backend)
const SYMBOL_MAP: Record<string, string> = {
  'APTOS': 'APT', 'ARBITRUM': 'ARB', 'OPTIMISM': 'OP', 'SOLANA': 'SOL',
  'STARKNET': 'STRK', 'IMMUTABLE-X': 'IMX', 'IMMUTABLE': 'IMX', 'SUI': 'SUI',
  'SEI-NETWORK': 'SEI', 'SEI': 'SEI', 'CELESTIA': 'TIA', 'DYDX': 'DYDX',
  'UNISWAP': 'UNI', 'AAVE': 'AAVE', 'MAKER': 'MKR', 'COMPOUND': 'COMP',
  'CURVE-DAO-TOKEN': 'CRV', 'SYNTHETIX-NETWORK-TOKEN': 'SNX', 'CHAINLINK': 'LINK',
  'POLYGON': 'MATIC', 'AVALANCHE': 'AVAX', 'NEAR': 'NEAR', 'COSMOS': 'ATOM',
  'POLKADOT': 'DOT', 'FILECOIN': 'FIL', 'THE-GRAPH': 'GRT', 'LIVEPEER': 'LPT',
  'RENDER-TOKEN': 'RNDR', 'FETCH-AI': 'FET', 'OCEAN-PROTOCOL': 'OCEAN',
  'SINGULARITYNET': 'AGIX', 'WORLDCOIN-WLD': 'WLD', 'PEPE': 'PEPE',
  'SHIBA-INU': 'SHIB', 'DOGECOIN': 'DOGE', 'FLOKI': 'FLOKI', 'BONK': 'BONK',
  'WIF': 'WIF', 'JUPITER-EXCHANGE-SOLANA': 'JUP', 'PYTH-NETWORK': 'PYTH',
  'JITO-GOVERNANCE-TOKEN': 'JTO', 'WORMHOLE': 'W', 'EIGENLAYER': 'EIGEN',
  'ETHER.FI': 'ETHFI', 'PENDLE': 'PENDLE', 'AEVO': 'AEVO', 'HYPERLIQUID': 'HYPE',
  'DIMENSION': 'DYM', 'KUJIRA': 'KUJI', 'INJECTIVE-PROTOCOL': 'INJ',
  'MANTA-NETWORK': 'MANTA', 'BLAST': 'BLAST', 'ZKSYNC': 'ZK', 'LINEA': 'LNA',
  'BASE': 'BASE', 'MANTLE': 'MNT', 'SCROLL': 'SCR', 'METIS': 'METIS',
  'FRAX-SHARE': 'FXS', 'LIDO-DAO': 'LDO', 'CONVEX-FINANCE': 'CVX',
  'YEARN-FINANCE': 'YFI', 'BALANCER': 'BAL', '1INCH': '1INCH', '0X': 'ZRX',
  'API3': 'API3', 'BAND-PROTOCOL': 'BAND', 'UMA': 'UMA', 'DECENTRALAND': 'MANA',
  'THE-SANDBOX': 'SAND', 'AXIE-INFINITY': 'AXS', 'ENJINCOIN': 'ENJ', 'GALA': 'GALA',
  'ILLUVIUM': 'ILV', 'GODS-UNCHAINED': 'GODS', 'YIELD-GUILD-GAMES': 'YGG',
  'MERIT-CIRCLE': 'MC', 'BIG-TIME': 'BIGTIME', 'ALIEN-WORLDS': 'TLM',
  'MY-NEIGHBOR-ALICE': 'ALICE', 'STAR-ATLAS': 'ATLAS', 'STEPN': 'GMT',
  'LOOKSRARE': 'LOOKS', 'BLUR': 'BLUR', 'NFTX': 'NFTX', 'GMX': 'GMX',
  'ENS': 'ENS', 'ROCKET-POOL': 'RPL',
};

function normalizeSymbol(raw: string): string {
  if (!raw) return '';
  const upper = raw.toUpperCase().trim();
  return SYMBOL_MAP[upper] || upper.replace(/-/g, '');
}

function formatTokens(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toLocaleString();
}

export default function DiscoverPage() {
  const { language } = useAppStore();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'rankings' | 'crypto-calendar' | 'economic-calendar' | 'sectors' | 'liq-heatmap'>('rankings');
  // v0.8: 下拉刷新 tick —— 传给 MarketTable 触发 refetch
  const [refreshTick, setRefreshTick] = useState(0);

  const t = {
    rankings: language !== 'en' ? '排行榜' : 'Rankings',
    cryptoCalendar: language !== 'en' ? '加密日历' : 'Crypto Calendar',
    economicCalendar: language !== 'en' ? '经济日历' : 'Economic Calendar',
    sectors: language !== 'en' ? '板块' : 'Sectors',
    liqHeatmap: language !== 'en' ? '清算热力图' : 'Liq. Heatmap',
  };

  return (
    <PullToRefresh
      onRefresh={async () => {
        qc.invalidateQueries({ queryKey: ['unlocks'] });
        qc.invalidateQueries({ queryKey: ['market-prices'] });
        qc.invalidateQueries({ queryKey: ['sectors'] });
        qc.invalidateQueries({ queryKey: ['liq-heatmap'] });
        setRefreshTick((v) => v + 1);
      }}
      refreshingLabel={language !== 'en' ? '发现页刷新中…' : 'Refreshing…'}
      pullLabel={language !== 'en' ? '下拉刷新' : 'Pull to refresh'}
    >
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{language !== 'en' ? '发现' : 'Discover'}</h1>
        {/* BUG06: 策略快捷入口（不改全站底栏结构） */}
        <Link
          href="/strategy"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gf-accent/40 bg-gf-accent/10 px-3 py-1.5 text-xs font-medium text-gf-accent transition-colors hover:bg-gf-accent/15"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 3 3 5-6" />
          </svg>
          {language !== 'en' ? '策略回测' : 'Strategy Backtest'}
        </Link>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto border-gf-border pb-1 lg:gap-0 lg:border-b lg:pb-0 lg:overflow-visible">
        <button
          onClick={() => setTab('rankings')}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors lg:rounded-none lg:border-0 lg:border-b-2 lg:px-4 lg:py-2 lg:text-sm ${
            tab === 'rankings'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent lg:bg-transparent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text lg:border-transparent lg:bg-transparent lg:text-gf-muted'
          }`}
        >
          {t.rankings}
        </button>
        <button
          onClick={() => setTab('crypto-calendar')}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors lg:rounded-none lg:border-0 lg:border-b-2 lg:px-4 lg:py-2 lg:text-sm ${
            tab === 'crypto-calendar'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent lg:bg-transparent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text lg:border-transparent lg:bg-transparent lg:text-gf-muted'
          }`}
        >
          {t.cryptoCalendar}
        </button>
        <button
          onClick={() => setTab('economic-calendar')}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors lg:rounded-none lg:border-0 lg:border-b-2 lg:px-4 lg:py-2 lg:text-sm ${
            tab === 'economic-calendar'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent lg:bg-transparent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text lg:border-transparent lg:bg-transparent lg:text-gf-muted'
          }`}
        >
          {t.economicCalendar}
        </button>
        <button
          onClick={() => setTab('sectors')}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors lg:rounded-none lg:border-0 lg:border-b-2 lg:px-4 lg:py-2 lg:text-sm ${
            tab === 'sectors'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent lg:bg-transparent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text lg:border-transparent lg:bg-transparent lg:text-gf-muted'
          }`}
        >
          {t.sectors}
        </button>
        <button
          onClick={() => setTab('liq-heatmap')}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors lg:rounded-none lg:border-0 lg:border-b-2 lg:px-4 lg:py-2 lg:text-sm ${
            tab === 'liq-heatmap'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent lg:bg-transparent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text lg:border-transparent lg:bg-transparent lg:text-gf-muted'
          }`}
        >
          {t.liqHeatmap}
        </button>
      </div>

      {tab === 'rankings' && <MarketTable refreshTick={refreshTick} />}
      {tab === 'crypto-calendar' && <CryptoCalendar />}
      {tab === 'economic-calendar' && <EconomicCalendarWidget />}
      {tab === 'sectors' && <SectorsView />}
      {tab === 'liq-heatmap' && <LiqHeatmap />}
    </div>
    </PullToRefresh>
  );
}

function CryptoCalendar() {
  const { language } = useAppStore();
  const lang = language !== 'en' ? 'zh_TW' : 'en';
  const isZh = language !== 'en';

  const { data, isLoading } = useQuery({
    queryKey: ['unlocks'],
    queryFn: () => api.get<{ upcoming: any[]; ended: any[]; source: string; totalTracked: number }>('/market/calendar/unlocks'),
    refetchInterval: 3600_000,
  });

  // Fetch market prices for unlock value calculation
  const { data: pricesData } = useQuery({
    queryKey: ['market-prices'],
    queryFn: () => api.get<any[]>('/market/markets?tab=all&pageSize=1000&sort=mcap&order=desc'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const priceMap: Record<string, number> = {};
  if (pricesData) {
    // Handle both array and object ({list: [...]}) response formats
    const list = Array.isArray(pricesData) ? pricesData : (pricesData as any).items || (pricesData as any).list || (pricesData as any).data || [];
    for (const item of list) {
      const sym = item.symbol?.toUpperCase() || '';
      if (sym && item.price) {
        priceMap[sym] = Number(item.price);
      }
    }
  }

  const upcoming = data?.upcoming || [];
  const ended = data?.ended || [];

  const t = {
    upcomingEvents: isZh ? '即将到来事件' : 'Upcoming Events',
    endedEvents: isZh ? '已结束事件' : 'Ended Events',
    tokensTracked: isZh ? '追踪代币总数' : 'Tokens Tracked',
    source: isZh ? '数据来源' : 'Source',
    upcomingUnlocks: isZh ? '即将到来的解锁' : 'Upcoming Unlocks',
    endedUnlocks: isZh ? '已结束的解锁' : 'Ended Unlocks',
    loading: isZh ? '加载中...' : 'Loading...',
    noData: isZh ? '暂无解锁数据' : 'No unlock data.',
    ended: isZh ? '已结束' : 'Ended',
    ofSupply: isZh ? '占供应量' : 'of supply',
    days: isZh ? '天' : 'd',
    viewChart: isZh ? '查看行情' : 'View Chart',
    unlockAmount: isZh ? '解锁数量' : 'Unlock Amount',
    unlockValue: isZh ? '解锁价值' : 'Unlock Value',
  };

  const formatUsd = (n: number): string => {
    if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
    return '$' + n.toFixed(2);
  };


  const renderUnlockCard = (e: any, isEnded: boolean) => {
    const d = new Date(e.date);
    const dayDiff = e.daysUntil ?? Math.ceil((d.getTime() - Date.now()) / 86400000);
    const rawSymbol = e.symbols?.[0] || '';
    const baseSymbol = normalizeSymbol(rawSymbol);
    const tokenStr = e.tokens ? formatTokens(Number(e.tokens)) : '—';
    const pctStr = e.pctSupply != null ? Number(e.pctSupply).toFixed(2) : '—';
    const coinName = e.title?.replace(/\s*unlock$/i, '') || baseSymbol;
    
    // Calculate unlock value
    const price = priceMap[`${baseSymbol}USDT`] || priceMap[`${baseSymbol}USDC`] || 0;
    const unlockValue = e.tokens && price > 0 ? Number(e.tokens) * price : 0;
    const valueStr = unlockValue > 0 ? formatUsd(unlockValue) : '—';

    return (
      <div
        key={`${e.slug || e.id || e.date}-${isEnded}`}
        className={`card relative overflow-hidden p-4 transition-all hover:border-gf-accent/40 ${
          isEnded ? 'opacity-60' : ''
        }`}
      >
        {/* Left accent bar */}
        <div className={`absolute left-0 top-0 h-full w-1 ${isEnded ? 'bg-gf-muted/30' : dayDiff <= 7 ? 'bg-gf-negative' : 'bg-gf-accent'}`} />

        <div className="flex items-start gap-3 pl-2">
          {/* Coin logo + name */}
          <Link href={`/coin/${baseSymbol}USDT`} className="flex shrink-0 items-center gap-3 hover:opacity-80">
            <div className="relative">
              <CoinLogo symbol={`${baseSymbol}USDT`} size={40} />
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-sm">{coinName}</div>
              <div className="text-[10px] text-gf-muted font-mono">{baseSymbol}/USDT</div>
            </div>
          </Link>

          {/* Date + countdown - more prominent */}
          <div className="ml-auto shrink-0 text-right">
            <div className={`text-base font-bold font-mono ${isEnded ? 'text-gf-muted' : dayDiff <= 7 ? 'text-gf-negative' : 'text-gf-text'}`}>
              {d.toLocaleDateString(lang === 'zh_TW' ? 'zh-TW' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="mt-0.5">
              {!isEnded ? (
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${dayDiff <= 7 ? 'bg-gf-negative/15 text-gf-negative' : 'bg-gf-surface2 text-gf-muted'}`}>
                  {dayDiff > 0 ? `${dayDiff} ${t.days}` : '—'}
                </span>
              ) : (
                <span className="inline-block rounded-full bg-gf-surface2 px-2 py-0.5 text-xs font-medium text-gf-muted">
                  {t.ended}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats row - 3 cards: amount, supply %, value */}
        <div className="mt-3 flex flex-wrap items-center gap-2 pl-2">
          <div className="rounded-lg bg-gf-surface2 px-3 py-1.5">
            <div className="text-[10px] text-gf-muted">{t.unlockAmount}</div>
            <div className="text-sm font-semibold font-mono">{tokenStr}</div>
          </div>
          <div className="rounded-lg bg-gf-surface2 px-3 py-1.5">
            <div className="text-[10px] text-gf-muted">{t.ofSupply}</div>
            <div className="text-sm font-semibold font-mono">{pctStr}%</div>
          </div>
          <div className="rounded-lg bg-gf-accent/10 px-3 py-1.5">
            <div className="text-[10px] text-gf-accent/70">{t.unlockValue}</div>
            <div className="text-sm font-semibold font-mono text-gf-accent">{valueStr}</div>
          </div>
          {!isEnded && (
            <Link
              href={`/coin/${baseSymbol}USDT`}
              className="ml-auto rounded-lg bg-gf-accent/10 px-3 py-1.5 text-xs font-medium text-gf-accent hover:bg-gf-accent/20"
            >
              {t.viewChart} →
            </Link>
          )}
        </div>
      </div>
    );
  };

  const groupByDate = (list: any[]) => {
    const map: Record<string, any[]> = {};
    for (const e of list) {
      const key = new Date(e.date).toDateString();
      (map[key] ||= []).push(e);
    }
    return Object.entries(map).sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());
  };

  const renderGrouped = (list: any[], isEnded: boolean) => (
    <div className="mb-8 space-y-4">
      {groupByDate(list).map(([dateKey, evts]) => (
        <div key={dateKey}>
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded-md bg-gf-surface2 px-2 py-0.5 text-xs font-semibold text-gf-text">
              {new Date(dateKey).toLocaleDateString(lang === 'zh_TW' ? 'zh-TW' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="text-[10px] text-gf-muted">{evts.length} {isZh ? '个事件' : 'events'}</span>
          </div>
          <div className="space-y-3">
            {evts.map((e) => renderUnlockCard(e, isEnded))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* Quick stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="card p-4">
          <div className="text-xs text-gf-muted">{t.upcomingEvents}</div>
          <div className="mt-1 text-2xl font-bold text-gf-warn">{upcoming.length || '—'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gf-muted">{t.endedEvents}</div>
          <div className="mt-1 text-2xl font-bold text-gf-muted">{ended.length || '—'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gf-muted">{t.tokensTracked}</div>
          <div className="mt-1 text-2xl font-bold">{data?.totalTracked || '—'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gf-muted">{t.source}</div>
          <div className="mt-1 text-2xl font-bold text-gf-accent">Oanor</div>
        </div>
      </div>

      {/* Upcoming unlocks */}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-lg">🔥</span>
        <h2 className="text-base font-semibold">{t.upcomingUnlocks}</h2>
      </div>
      {isLoading ? (
        <ListSkeleton rows={5} />
      ) : upcoming.length === 0 ? (
        <div className="card">
          <EmptyState
            title={t.noData}
            description={isZh ? '近期没有代币解锁事件' : 'No upcoming token unlock events.'}
          />
        </div>
      ) : (
        renderGrouped(upcoming, false)
      )}

      {/* Ended unlocks */}
      {ended.length > 0 && (
        <>
          <div className="mb-2 mt-6 flex items-center gap-2">
            <span className="text-lg">✅</span>
            <h2 className="text-base font-semibold text-gf-muted">{t.endedUnlocks}</h2>
          </div>
          {renderGrouped(ended, true)}
        </>
      )}
    </div>
  );
}

function EconomicCalendarWidget() {
  return <EconomicCalendar />;
}
